import { getLoadedBoard } from '../../Config/Levels';
import BoardArrangement, { IBoardMap, TBoard } from '../../Game/BoardArrangement';
import BoardConnections from '../../Game/BoardConnections';
import BoardSizing from '../../Game/BoardSizing';
import BoardUtils, { IBoardData, Pair } from '../../Game/BoardUtils';
import BoardObject from '../../Tiles/BoardObject';
import Tile from '../../Tiles/Tile';
import { ShuffleBlacklist, Suits } from '../../Tiles/TileSet';
import { SeedGenerator } from '../../Utils/SeedGenerator';
import { Rng } from '../../Utils/Rng';
import { getRandom } from './boardHelpers';
import * as fromActions from '../Actions/Actions';
import { ActionTypes } from '../Actions/Actions';

export interface IStateBoard {
    boardTiles: Array<Tile | BoardObject>;
    boardMap: IBoardMap;
    boardSize: [Pair, Pair];
    available: string[];
    availableMatches: Tile[];
    ready: boolean;
    blocked: boolean;
}

export const defaultStateBoard: IStateBoard = {
    available: [],
    boardMap: {},
    boardSize: [[7, 8], [0, 0]],
    boardTiles: [],
    availableMatches: [],
    ready: false,
    blocked: false,
};

export const StateBoard = (state = defaultStateBoard, action: fromActions.Actions): IStateBoard => {
    switch (action.type) {
        case ActionTypes.BOARD_READY:
            return {
                ...state,
                ready: action.payload,
            };
        case ActionTypes.BLOCK_BOARD:
            return {
                ...state,
                blocked: action.payload,
            };
        case ActionTypes.START_LEVEL:
            return initBoard();
        case ActionTypes.REMOVE_MATCH:
            return removeFromBoard(state, action.payload[0]);
        case ActionTypes.REMOVE_STORED:
            return removeFromBoard(state, [action.payload]);
        case ActionTypes.COUNT_OBJECTS:
            return removeFromBoard(state, action.payload);
        case ActionTypes.LOAD_LEVEL:
            return initBoard();
        case ActionTypes.REMOVE_TILE:
            return removeFromBoard(state, [action.payload]);
        case ActionTypes.SHUFFLE_TILES:
            const tiles = shuffleTiles(state);
            const boardMap = BoardUtils.createBoardMap(tiles);
            const available = BoardConnections.getAvailableForBoard(
                tiles,
                Object.values(boardMap).map(t => t.locId)
            );
            return {
                ...state,
                boardTiles: tiles,
                available,
                boardMap,
                boardSize: BoardSizing.getLevelVisualUnits(tiles.map(t => t.position)),
                availableMatches: BoardUtils.findMatchesFromAvailable(available, boardMap),
            };
        case ActionTypes.SET_TILESPRITES:
            const mappedTiles = mapSpritesToBoard(state.boardTiles);
            const newMap = BoardUtils.createBoardMap(mappedTiles);
            return {
                ...state,
                boardTiles: mappedTiles,
                boardMap: newMap,
            };
        case ActionTypes.CHANGE_VIEW:
            return {
                ...state,
                ready: false,
            };
        default:
            return state;
    }
};

const initBoard = (seed?: string): IStateBoard => {
    // store somewhere in INIT_LEVEL
    const loadedBoard: IBoardData = getLoadedBoard();
    if (!loadedBoard) {
        return defaultStateBoard;
    }
    Rng.clearGlobals();
    const seedPool = loadedBoard.config.seeds;
    const randomSeed = seedPool
        ? seedPool[getRandom(0, seedPool.length - 1)]
        : SeedGenerator.randomSeed();
    seed = seed || randomSeed;
    let boardTiles = createBoard(loadedBoard);
    const positions: number[][] = getBoardPositions(boardTiles);
    const maxLayer: number = BoardSizing.getHighestLayer(positions);
    boardTiles = calculateAllBlocksAndSupports(boardTiles, maxLayer);

    let normalTiles = boardTiles.filter(t => t.tileType.suit !== 'special') as Tile[];
    const objectTiles = boardTiles.filter(t => t.tileType.suit === 'special') as BoardObject[];
    normalTiles = BoardArrangement.fillBoard(
        normalTiles,
        loadedBoard.config,
        objectTiles,
        seed
    ) as Tile[];

    const base = [...normalTiles, ...objectTiles];
    const boardAndObjects = sortBoard(base);

    const boardMap = BoardUtils.createBoardMap(boardAndObjects);
    const available = BoardConnections.getAvailableForBoard(
        boardAndObjects,
        Object.values(boardMap).map(m => m.locId)
    );
    return {
        available,
        boardMap,
        availableMatches: BoardUtils.findMatchesFromAvailable(available, boardMap),
        boardSize: BoardSizing.getLevelVisualUnits(boardAndObjects.map(t => t.position)),
        boardTiles: boardAndObjects,
        ready: false,
        blocked: false,
    };
};

const sortBoard = (board: Array<Tile | BoardObject>): Array<Tile | BoardObject> => {
    const sorted = board.sort((a, b) => {
        return (
            a.position[0] - b.position[0] ||
            a.position[1] + a.position[2] - (b.position[1] + b.position[2])
        );
    });
    return sorted;
};

export const getBoardPositions = (tiles: TBoard): number[][] => {
    return tiles.reduce((acc, t) => {
        const z = [...t.position];
        acc.push(z);
        return acc;
    }, []);
};

const removeFromBoard = (state: IStateBoard, removeIds: string[]): IStateBoard => {
    let newBoard = state.boardTiles.filter(tile => removeIds.indexOf(tile.boardId) < 0);
    let newMap = BoardUtils.createBoardMap(newBoard);
    const adjusted = adjustPositions(newBoard, newMap);
    if (adjusted[1].length > 0) {
        newMap = BoardUtils.createBoardMap(adjusted[0]);
        newBoard = adjusted[0];
    }
    const newAvailable = BoardConnections.getAvailableForBoard(
        newBoard,
        Object.values(newMap).map(m => m.locId)
    );
    const availableMatches = BoardUtils.findMatchesFromAvailable(newAvailable, newMap);

    return {
        ...state,
        available: newAvailable,
        boardTiles: sortBoard(newBoard),
        boardMap: newMap,
        boardSize: BoardSizing.getLevelVisualUnits(newBoard.map(t => t.position)),
        availableMatches,
    };
};

const shuffleTiles = (prevState: IStateBoard) => {
    let tiles = prevState.boardTiles.slice(0);
    let shuffles = tiles.filter(
        t => !ShuffleBlacklist.includes(t.tileType.suit as Suits)
    ) as Tile[];
    let specials = tiles.filter(t =>
        ShuffleBlacklist.includes(t.tileType.suit as Suits)
    ) as BoardObject[];

    // if there is only one tile to select in the current arrangement, tiles need to be repositioned instead of just scrambled
    if (prevState.available.length === 1) {
        const swapPositions = BoardArrangement.getFreeSpaces(
            prevState,
            shuffles.length + specials.length
        );
        const maxLayer = BoardSizing.getHighestLayer(getBoardPositions(tiles));
        shuffles = shuffles.reduce((acc, tile) => {
            const newPosition = swapPositions.splice(getRandom(0, swapPositions.length - 1), 1)[0];

            tile.locId = BoardUtils.formatBoardId(newPosition);
            tile.layer = newPosition[0];
            tile.position = newPosition;
            tile.blockPositions = BoardConnections.calculateBlockPositions(tile.position, maxLayer);
            tile.supportPositions = BoardConnections.calculateSupportPositions(newPosition);
            acc.push(tile);
            return acc;
        }, []);

        specials = specials.map(s => {
            s.position = swapPositions.splice(getRandom(0, swapPositions.length - 1), 1)[0];
            s.layer = s.position[0];
            s.locId = BoardUtils.formatBoardId(s.position);
            s.blockPositions = BoardConnections.calculateBlockPositions(s.position, maxLayer);
            s.supportPositions = BoardConnections.calculateSupportPositions(s.position);
            return s;
        });
    }

    tiles = getShuffled(shuffles, specials);
    return sortBoard([...tiles, ...specials]);
};

const getShuffled = (tiles: Tile[], specials: BoardObject[]): TBoard => {
    return BoardArrangement.shuffleBoard(tiles, specials);
};

// moved to boardHelpers for testability
export const createBoard = (levelData: IBoardData): (Tile | BoardObject)[] => {
    const tiles = levelData.tiles.slice(0).reduce(
        (acc, tile) => {
            const boardTile = new Tile(tile.position, tile.type, acc.length.toString());
            acc.push(boardTile);
            return acc;
        },
        [] as Tile[]
    );

    if (!levelData.objects) {
        return [...tiles];
    }

    const objects = levelData.objects.slice(0).reduce(
        (acc, o) => {
            const boardObject = new BoardObject(o.position, (tiles.length + acc.length).toString());
            acc.push(boardObject);
            return acc;
        },
        [] as BoardObject[]
    );

    return [...tiles, ...objects];
};

// maps data tiles to sprites created in view
const mapSpritesToBoard = (board: TBoard): TBoard => {
    return board.map(tile => {
        if (tile.tileType.suit !== 'special') {
            tile.tileSprite = BoardUtils.formatId(tile.tileType);
        }
        return tile;
    });
};

// reconfigures board when tiles are pulled out of it, ie magnet
// checks support positions of each tile and
const adjustPositions = (board: TBoard, m: IBoardMap): [TBoard, TBoard] => {
    const mapKeys = Object.values(m).map(k => k.locId);
    const shifted: TBoard = [];
    const adjusted = board.map(t => {
        const n = { ...t };
        if (n.supportPositions.includes('FLOOR')) {
            return n;
        }
        const supports = n.supportPositions.filter(f => mapKeys.includes(f));
        if (supports.length === 0) {
            const loc = n.position;
            n.position = [loc[0] - 1, loc[1], loc[2]];
            n.locId = BoardUtils.formatBoardId(n.position);
            n.supportPositions = BoardConnections.calculateSupportPositions(n.position);
            n.blockPositions = BoardConnections.calculateBlockPositions(
                n.position,
                BoardSizing.getHighestLayer(getBoardPositions(board))
            );

            shifted.push(n);
        }

        return n;
    });

    return [adjusted, shifted];
};

export const calculateAllBlocksAndSupports = (b: TBoard, maxLayer: number) => {
    return b.map(tile => {
        return {
            ...tile,
            blockPositions: BoardConnections.calculateBlockPositions(tile.position, maxLayer),
            supportPositions: BoardConnections.calculateSupportPositions(tile.position),
        };
    });
};
